import { useState, useMemo } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Info } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterExpressionBuilder } from './FilterExpressionBuilder'
import { FormulaPreview } from './FormulaPreview'
import { useStoreAttributes, usePogGroups } from '@/hooks/useStoreAttributes'
import { useHierarchyLabelMap } from '@/hooks/useHierarchy'
import type {
  StrongModelCriteria,
  AggregationMethod,
  FilterGroup,
} from '@/types'
import type { HierarchyLevel } from '@/api/productHierarchy'

const PRODUCT_FIELDS = [
  'division_id',
  'department_id',
  'class_id',
  'subclass_id',
]
const PRODUCT_FIELD_LABELS: Record<string, string> = {
  division_id: 'Division',
  department_id: 'Department',
  class_id: 'Class',
  subclass_id: 'Subclass',
}

const PRODUCT_HIERARCHY_FIELDS: Record<string, HierarchyLevel> = {
  division_id: 'DIVISION',
  department_id: 'DEPARTMENT',
  class_id: 'CLASS',
  subclass_id: 'SUBCLASS',
}

interface CriteriaBuilderProps {
  criteria: StrongModelCriteria
  onChange: (criteria: StrongModelCriteria) => void
  disabled?: boolean
  readOnly?: boolean
}

export function CriteriaBuilder({
  criteria,
  onChange,
  disabled,
  readOnly,
}: CriteriaBuilderProps) {
  const { data: storeAttrsData, isLoading: storeAttrsLoading } =
    useStoreAttributes()
  const { data: pogGroupsData, isLoading: pogGroupsLoading } = usePogGroups()
  const hierarchyLabelMap = useHierarchyLabelMap()

  const storeFields = useMemo(
    () => (storeAttrsData?.attributes ?? []).map(a => a.name),
    [storeAttrsData]
  )

  const storeFieldLabels = useMemo(
    () =>
      Object.fromEntries(
        (storeAttrsData?.attributes ?? []).map(a => [a.name, a.label])
      ),
    [storeAttrsData]
  )

  const categoricalFieldsSet = useMemo(
    () =>
      new Set(
        (storeAttrsData?.attributes ?? [])
          .filter(a => a.type === 'categorical')
          .map(a => a.name)
      ),
    [storeAttrsData]
  )

  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const markTouched = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const updateTime = (updates: Partial<StrongModelCriteria['time']>) => {
    onChange({
      ...criteria,
      time: { ...criteria.time, ...updates },
    })
  }

  // Validation helpers
  const startDateError =
    touched.start_date && !criteria.time.start_date
      ? 'Start date is required'
      : null
  const endDateError =
    touched.end_date && !criteria.time.end_date
      ? 'End date is required'
      : touched.end_date &&
          criteria.time.start_date &&
          criteria.time.end_date &&
          criteria.time.end_date <= criteria.time.start_date
        ? 'End date must be after start date'
        : null

  const isDisabled = disabled || readOnly

  return (
    <fieldset disabled={isDisabled} className="flex flex-col gap-6">
      {/* Time Criteria */}
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-foreground">Time Criteria</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="start_date">Start Date{!readOnly && ' *'}</Label>
            <Input
              id="start_date"
              type="date"
              value={criteria.time.start_date ?? ''}
              onChange={e => updateTime({ start_date: e.target.value || null })}
              onBlur={() => markTouched('start_date')}
            />
            {!readOnly && startDateError && (
              <p className="text-sm text-destructive">{startDateError}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="end_date">End Date{!readOnly && ' *'}</Label>
            <Input
              id="end_date"
              type="date"
              value={criteria.time.end_date ?? ''}
              onChange={e => updateTime({ end_date: e.target.value || null })}
              onBlur={() => markTouched('end_date')}
            />
            {!readOnly && endDateError && (
              <p className="text-sm text-destructive">{endDateError}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="aggregation_method">Aggregation Method</Label>
            <Select
              value={criteria.time.aggregation_method}
              onValueChange={v =>
                updateTime({ aggregation_method: v as AggregationMethod })
              }
              disabled={isDisabled}
            >
              <SelectTrigger id="aggregation_method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TOTAL">Total</SelectItem>
                <SelectItem value="AVG_WEEKLY">Avg Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Product Expression */}
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-foreground">
          Product Expression
        </h3>
        {criteria.product_expression ? (
          <>
            <FormulaPreview
              group={criteria.product_expression}
              fieldLabels={PRODUCT_FIELD_LABELS}
              valueLabels={hierarchyLabelMap}
            />
            <FilterExpressionBuilder
              group={criteria.product_expression}
              onChange={(group: FilterGroup) =>
                onChange({ ...criteria, product_expression: group })
              }
              fields={PRODUCT_FIELDS}
              fieldLabels={PRODUCT_FIELD_LABELS}
              hierarchyFields={PRODUCT_HIERARCHY_FIELDS}
              readOnly={readOnly}
            />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">None</p>
        )}
      </div>

      {/* Store Expression */}
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-foreground">
          Store Expression
        </h3>
        {!readOnly && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Add no criteria to select all stores</span>
          </div>
        )}
        {criteria.location_expression ? (
          storeAttrsLoading ? (
            <p className="text-sm text-muted-foreground">
              Loading attributes...
            </p>
          ) : (
            <>
              <FormulaPreview
                group={criteria.location_expression}
                fieldLabels={storeFieldLabels}
              />
              <FilterExpressionBuilder
                group={criteria.location_expression}
                onChange={(group: FilterGroup) =>
                  onChange({ ...criteria, location_expression: group })
                }
                fields={storeFields}
                fieldLabels={storeFieldLabels}
                storeAttributeFields={categoricalFieldsSet}
                readOnly={readOnly}
              />
            </>
          )
        ) : (
          readOnly && <p className="text-sm text-muted-foreground">None</p>
        )}
      </div>

      {/* POG Group */}
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-foreground">POG Group</h3>
        {!readOnly && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>
              Select a POG Group to filter stores by planogram assignment
            </span>
          </div>
        )}
        {pogGroupsLoading ? (
          <p className="text-sm text-muted-foreground">Loading POG groups...</p>
        ) : (
          <Select
            value={criteria.pog_group ?? '__none__'}
            onValueChange={v =>
              onChange({ ...criteria, pog_group: v === '__none__' ? null : v })
            }
            disabled={isDisabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="None (no POG group filter)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {(pogGroupsData?.values ?? []).map(group => (
                <SelectItem key={group} value={group}>
                  {group}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </fieldset>
  )
}
